import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { forgotPassword } from '../../slice/authSlice';
import Swal from 'sweetalert2';

// Validation Schema
const forgotSchema = yup.object().shape({
    email: yup
        .string()
        .required('Please enter your email address')
        .email('Please enter a valid email address'),
});

const ForgetPass = () => {
    const [submitted, setSubmitted] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(forgotSchema),
        mode: 'onBlur',
    });

    const onSubmit = async (data) => {
        try {
            const response = await dispatch(
                forgotPassword({
                    payload: { ...data },
                })
            );

            if (response?.payload?.code === 200) {
                setSubmittedEmail(data.email);
                setSubmitted(true);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message || 'Reset link sent successfully!',
                    showConfirmButton: false,
                    timer: 3000,
                });
            } else {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    backdrop: false,
                    title: response?.payload?.message || 'Something went wrong. Please try again.',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                backdrop: false,
                title: 'An unexpected error occurred. Please try again.',
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };

    return (
        <>
            <style>{`
                .forgot-wrapper {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
                    position: relative;
                    overflow: hidden;
                    padding: 20px;
                }

                .forgot-wrapper::before {
                    content: '';
                    position: absolute;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
                    top: -100px;
                    right: -100px;
                    pointer-events: none;
                }

                .forgot-wrapper::after {
                    content: '';
                    position: absolute;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
                    bottom: -80px;
                    left: -80px;
                    pointer-events: none;
                }

                .forgot-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: 40px 36px;
                    width: 100%;
                    max-width: 420px;
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
                    animation: slideUp 0.5s ease forwards;
                    position: relative;
                    z-index: 1;
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .forgot-icon-wrap {
                    width: 64px;
                    height: 64px;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
                }

                .forgot-icon-wrap svg {
                    width: 28px;
                    height: 28px;
                    fill: none;
                    stroke: #fff;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                }

                .forgot-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #fff;
                    margin-bottom: 6px;
                    letter-spacing: -0.3px;
                }

                .forgot-subtitle {
                    font-size: 0.88rem;
                    color: rgba(255, 255, 255, 0.55);
                    margin-bottom: 0;
                    line-height: 1.5;
                }

                .forgot-label {
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.75);
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                }

                .forgot-input {
                    background: rgba(255, 255, 255, 0.07) !important;
                    border: 1.5px solid rgba(255, 255, 255, 0.12) !important;
                    border-radius: 10px !important;
                    color: #fff !important;
                    padding: 12px 16px !important;
                    font-size: 0.93rem !important;
                    transition: all 0.25s ease !important;
                    height: auto !important;
                }

                .forgot-input::placeholder {
                    color: rgba(255, 255, 255, 0.3) !important;
                }

                .forgot-input:focus {
                    background: rgba(255, 255, 255, 0.1) !important;
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
                    outline: none !important;
                }

                .forgot-input.is-invalid {
                    border-color: #f87171 !important;
                    box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15) !important;
                }

                .forgot-error {
                    font-size: 0.78rem;
                    color: #f87171;
                    margin-top: 5px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .forgot-btn {
                    background: linear-gradient(135deg, #6366f1, #8b5cf6) !important;
                    border: none !important;
                    border-radius: 10px !important;
                    padding: 13px !important;
                    font-size: 0.93rem !important;
                    font-weight: 600 !important;
                    color: #fff !important;
                    width: 100%;
                    transition: all 0.25s ease !important;
                    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4) !important;
                    letter-spacing: 0.3px;
                }

                .forgot-btn:hover:not(:disabled) {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.5) !important;
                    background: linear-gradient(135deg, #5254cc, #7c3aed) !important;
                }

                .forgot-btn:disabled {
                    opacity: 0.7 !important;
                    cursor: not-allowed !important;
                }

                .btn-spinner {
                    display: inline-block;
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                    margin-right: 8px;
                    vertical-align: middle;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .divider {
                    border-color: rgba(255, 255, 255, 0.08);
                    margin: 24px 0;
                }

                .back-to-login {
                    color: rgba(255, 255, 255, 0.55) !important;
                    text-decoration: none !important;
                    font-size: 0.87rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: color 0.2s ease;
                }

                .back-to-login:hover {
                    color: #a5b4fc !important;
                }

                .back-to-login svg {
                    width: 15px;
                    height: 15px;
                    stroke: currentColor;
                    fill: none;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    transition: transform 0.2s ease;
                }

                .back-to-login:hover svg {
                    transform: translateX(-3px);
                }

                /* Success State */
                .success-state {
                    animation: slideUp 0.4s ease forwards;
                }

                .success-icon-wrap {
                    width: 72px;
                    height: 72px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
                }

                .success-icon-wrap svg {
                    width: 32px;
                    height: 32px;
                    fill: none;
                    stroke: #fff;
                    stroke-width: 2.5;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-dasharray: 60;
                    stroke-dashoffset: 0;
                    animation: checkDraw 0.5s ease 0.1s both;
                }

                @keyframes checkDraw {
                    from { stroke-dashoffset: 60; }
                    to { stroke-dashoffset: 0; }
                }

                .email-highlight {
                    background: rgba(99, 102, 241, 0.15);
                    border: 1px solid rgba(99, 102, 241, 0.3);
                    border-radius: 8px;
                    padding: 10px 14px;
                    color: #a5b4fc;
                    font-size: 0.88rem;
                    font-weight: 500;
                    word-break: break-all;
                    margin: 16px 0;
                }

                .resend-hint {
                    font-size: 0.82rem;
                    color: rgba(255, 255, 255, 0.4);
                    line-height: 1.5;
                }

                .logo-wrap img {
                    height: 50px;
                    object-fit: contain;
                }
            `}</style>

            <div className="forgot-wrapper">
                <div className="forgot-card">

                    {/* Logo */}
                    <div className="text-center mb-4 logo-wrap">
                        <Link to="/">
                            <img
                                src={process.env.PUBLIC_URL + '/assets/images/logo-new.svg'}
                                alt="Logo"
                            />
                        </Link>
                    </div>

                    {!submitted ? (
                        /* ── Form State ── */
                        <>
                            <div className="text-center mb-4">
                                <div className="forgot-icon-wrap">
                                    <svg viewBox="0 0 24 24">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                                <h4 className="forgot-title">Forgot Password?</h4>
                                <p className="forgot-subtitle">
                                    No worries! Enter your registered email and we'll send you a reset link.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                <div className="mb-4">
                                    <label htmlFor="email" className="forgot-label">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        className={`form-control forgot-input ${errors.email ? 'is-invalid' : ''}`}
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        {...register('email')}
                                    />
                                    {errors.email && (
                                        <div className="forgot-error">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="8" x2="12" y2="12" />
                                                <line x1="12" y1="16" x2="12.01" y2="16" />
                                            </svg>
                                            {errors.email.message}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="forgot-btn"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting && <span className="btn-spinner" />}
                                    {isSubmitting ? 'Sending Reset Link...' : 'Send Reset Link'}
                                </button>
                            </form>

                            <hr className="divider" />

                            <div className="text-center">
                                <Link to="/" className="back-to-login">
                                    <svg viewBox="0 0 24 24">
                                        <line x1="19" y1="12" x2="5" y2="12" />
                                        <polyline points="12 19 5 12 12 5" />
                                    </svg>
                                    Back to Login
                                </Link>
                            </div>
                        </>
                    ) : (
                        /* ── Success State ── */
                        <div className="text-center success-state">
                            <div className="success-icon-wrap">
                                <svg viewBox="0 0 24 24">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>

                            <h4 className="forgot-title">Check Your Inbox</h4>
                            <p className="forgot-subtitle">
                                We've sent a password reset link to:
                            </p>

                            <div className="email-highlight">{submittedEmail}</div>

                            <p className="resend-hint mb-4">
                                Didn't receive it? Check your spam folder or wait a few minutes before trying again.
                            </p>

                            <hr className="divider" />

                            <Link to="/login" className="back-to-login">
                                <svg viewBox="0 0 24 24">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back to Login
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ForgetPass;